import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { ImageDto } from '@/core/data/location/location.interface';
import { BlobSource } from '@/core/data/report/report.type';
import { gambits, localize } from '@/core/gambits';
import { useAuthStore } from '@/stores/auth.store';
import type GamExpandImage from '@/views/components/GamExpandImage.vue';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import { isHandshakeLive } from '@/views/main/handshakes/utils/handshake-game';
import { photosInSlot, photosToUpload } from '@/views/main/handshakes/utils/handshake-photos';
import { useFileDialog, useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch, type Ref } from 'vue';
import { useToast } from 'vue-toastification';

export interface PositionPhotosOptions {
  /** The handshake the photos go on. Null while nobody is seated. */
  handshakeId: Ref<string | null | undefined>;
  position: Ref<number | null>;
  /** Whether the position is already on the handshake, so a photo does not write it again. */
  isPlayed: (position: number) => boolean;
  /** A photo is proof of the game, so the position is saved before it goes up. */
  markPlayed: (handshakeId: string, position: number) => Promise<boolean>;
}

/** The last copy of each handshake seen this visit, so a sheet opens on its photos instead of waiting for the list. */
const knownHandshakes = new Map<string, HandshakeDto>();

/** Keep handshakes a screen already loaded, so their photos are there the moment a sheet opens on one. */
export function rememberPhotoHandshakes(handshakes: HandshakeDto[]): void {
  for (const item of handshakes) knownHandshakes.set(item.id, item);
}

/**
 * The photos on one position of one handshake. The recommendation and the clock
 * both read it, and an upload from either is heard by both, so they show the same.
 */
export function usePositionPhotos({ handshakeId, position, isPlayed, markPlayed }: PositionPhotosOptions) {
  const { user: authUser } = storeToRefs(useAuthStore());
  const toast = useToast();
  const now = useNow({ interval: 60_000 });

  const handshake = ref<HandshakeDto | null>(null);
  const viewer = ref<GamExpandImageType | null>(null);
  const expand = ref<InstanceType<typeof GamExpandImage>>();
  const { upload, lastSettled, isUploading, settle } = useHandshakePhotoUpload();

  const busy = computed(() => !!handshakeId.value && isUploading(handshakeId.value));

  /** Newest first. Either player's photo counts. */
  const slotPhotos = computed(() =>
    (handshake.value?.photos ?? [])
      .filter((photo) => photo.positionIndex === position.value)
      .sort((left, right) => (right.createdAt ?? '').localeCompare(left.createdAt ?? '')),
  );

  const src = computed(() => {
    const hash = slotPhotos.value[0]?.imageBlobHash;

    return hash ? gambits.imageService.getImageSrc(hash, 64) : null;
  });

  const canAdd = computed(() => {
    const me = authUser.value?.id;
    if (!handshakeId.value || !me || position.value == null) return false;

    return photosInSlot(handshake.value ?? {}, me, position.value) < 5;
  });

  /** The image icon only while this handshake is still open. Add photo stays under the three dots after it closes. */
  const shortcut = computed(() => {
    const createdAt = handshake.value?.createdAt;
    if (!canAdd.value || !createdAt) return false;

    return isHandshakeLive(createdAt, now.value);
  });

  const load = async () => {
    const id = handshakeId.value;
    const userId = authUser.value?.id;
    if (!id || !userId) {
      handshake.value = null;
      return;
    }

    if (handshake.value?.id !== id) handshake.value = knownHandshakes.get(id) ?? null;

    const result = await gambits.userService.getHandshakes(userId);
    if (handshakeId.value !== id) return;

    if (!result.data) return;

    rememberPhotoHandshakes(result.data);
    handshake.value = result.data.find((item) => item.id === id) ?? null;
  };

  watch([handshakeId, () => authUser.value?.id], () => void load(), { immediate: true });

  watch(lastSettled, (settled) => {
    if (!settled || settled.handshakeId !== handshakeId.value) return;

    void load();
  });

  const { open: openDialog, onChange: onPicked } = useFileDialog({
    accept: 'image/*',
    multiple: true,
    reset: true,
  });

  const images = (): ImageDto[] => {
    const current = handshake.value;
    if (!current) return [];

    return slotPhotos.value.map((photo) => ({
      id: photo.imageBlobHash,
      imageBlobHash: photo.imageBlobHash,
      liked: false,
      user:
        photo.userId === current.initiatorId
          ? current.initiator
          : photo.userId === current.responderId
            ? current.responder
            : null,
      createdAt: photo.createdAt ?? current.createdAt,
      timezone: current.timezone || 'UTC',
      handshakeId: current.id,
      positionIndex: photo.positionIndex,
    }));
  };

  const openGallery = () => {
    const shown = images();
    if (!shown.length) return;

    viewer.value = {
      blobSource: BlobSource.HANDSHAKE,
      images: shown,
      selectedIndex: 0,
      noFilter: true,
    };
    nextTick(() => {
      expand.value?.show();
    });
  };

  watch(handshake, () => {
    if (!viewer.value) return;

    const shown = images();
    if (!shown.length) {
      viewer.value = null;
      return;
    }

    const current = viewer.value.images[viewer.value.selectedIndex]?.imageBlobHash;
    const index = shown.findIndex((image) => image.imageBlobHash === current);
    viewer.value = {
      ...viewer.value,
      images: shown,
      selectedIndex: index >= 0 ? index : 0,
    };
  });

  /** Another photo, from the gallery. */
  const addAnother = () => {
    if (!canAdd.value) {
      toast.error(localize('handshake.photo.taken'));
      return;
    }

    openDialog();
  };

  /** The newest photo opens the gallery. Without one, the camera roll. */
  const open = () => {
    if (busy.value) return;

    if (src.value) {
      openGallery();
      return;
    }

    addAnother();
  };

  const take = async (files: FileList | File[] | null) => {
    const id = handshakeId.value;
    const me = authUser.value?.id ?? null;
    const slot = position.value;
    if (!files?.length || !id || slot == null) return;

    const already = photosInSlot(handshake.value ?? {}, me, slot);
    const batch = photosToUpload(files, already);
    if (!batch.length || batch.length < files.length) toast.error(localize('handshake.photo.taken'));
    if (!batch.length) return;

    if (!isPlayed(slot) && !(await markPlayed(id, slot))) return;

    const saved = await upload(id, batch, slot);
    if (saved) handshake.value = saved;
  };

  onPicked((files) => {
    void take(files);
  });

  const closeGallery = () => {
    viewer.value = null;
  };

  const remove = async (hash: string) => {
    const id = handshakeId.value;
    if (!id || !hash) return;

    const result = await gambits.userService.removeHandshakeImage(id, hash);
    if (result.error) {
      toast.error(result.error.message || localize('handshake.photo.deleteFailed'));
      return;
    }

    if (handshake.value?.id === id) {
      handshake.value = {
        ...handshake.value,
        photos: (handshake.value.photos ?? []).filter((photo) => photo.imageBlobHash !== hash),
      };
    }
    settle(id);
  };

  return { handshake, src, busy, canAdd, shortcut, viewer, expand, open, addAnother, closeGallery, remove };
}
